import React from 'react';
import MailingListUpdate from "./MailingListUpdate";
import MailingListCategory from "./MailingListCategory";
import { IMailingList } from "./MailingListItem";
import { IListCategory } from "./MailingListCategory";
import PciStatusAlert from "../PciStatusAlert";
import { PciDataFreshness, PciDataStatus, PciFreshnessState, PciResponseHeaders } from "../../pciDataStatus";

interface IMailingListProps { }

interface State {
    listCategories: Array<IListCategory>;
    mailingLists: Array<IMailingList>;
    subOnly: boolean;
    isLoading: boolean;
    isReady: boolean;
    loadError: string | null;
    catalogFreshness: PciDataFreshness | null;
    catalogState: PciFreshnessState | null;
}

interface IPciResponse<T> {
    data: T;
    freshness: PciDataFreshness | null;
}

export class MailingList extends React.Component<IMailingListProps, State> {
    private mounted = false;
    private stopWatchingCatalog: (() => void) | null = null;

    constructor(props: IMailingListProps) {
        super(props);
        this.state = {
            listCategories: [],
            mailingLists: [],
            subOnly: true,
            isLoading: false,
            isReady: false,
            loadError: null,
            catalogFreshness: null,
            catalogState: null
        };
        this.SubscribeList = this.SubscribeList.bind(this);
        this.UpdateSubscribedLists = this.UpdateSubscribedLists.bind(this);
    }

    public componentDidMount() {
        this.mounted = true;
        void this.LoadMailingLists();
    }

    public componentWillUnmount() {
        this.mounted = false;
        this.stopWatchingCatalog?.();
    }

    private Request<T>(url: string, requiresFreshness: boolean): Promise<IPciResponse<T>> {
        return new Promise((resolve, reject) => {
            $.ajax({
                url,
                type: "GET",
                dataType: "json",
                success: (data: T, _status: string, xhr: PciResponseHeaders) => {
                    const freshness = PciDataStatus.FromResponse(xhr);
                    if (requiresFreshness && !freshness) {
                        reject(new Error("PCI freshness information is missing."));
                        return;
                    }
                    resolve({ data, freshness });
                },
                error: () => reject(new Error("PCI request failed."))
            });
        });
    }

    private async LoadMailingLists() {
        this.setState({ isLoading: true, isReady: false, loadError: null });

        try {
            const [categoriesResponse, catalogResponse, subscriptionsResponse] = await Promise.all([
                this.Request<Array<IListCategory>>("/api/MailingLists/GetMailingListCategories", true),
                this.Request<Array<IMailingList>>("/api/MailingLists/GetMailingLists", true),
                this.Request<Array<IMailingList>>("/api/MailingLists/GetMailingListsForCurrentUser", false)
            ]);
            if (!this.mounted) return;

            const catalogFreshness = PciDataStatus.Combine(
                categoriesResponse.freshness, catalogResponse.freshness);
            if (!catalogFreshness) {
                throw new Error("PCI freshness information is missing.");
            }

            const subscriptions = new Map(
                subscriptionsResponse.data.map(list => [list.listName, list] as const));
            const mailingLists = catalogResponse.data.map(list => {
                const subscription = subscriptions.get(list.listName);
                return {
                    ...list,
                    subscribed: subscription !== undefined,
                    subscribe: subscription !== undefined,
                    subscriptionDate: subscription ? new Date(subscription.subscriptionDate) : null
                };
            });

            this.setState({
                listCategories: categoriesResponse.data,
                mailingLists,
                isLoading: false,
                isReady: true,
                loadError: null,
                catalogFreshness,
                catalogState: catalogFreshness.status
            });

            this.stopWatchingCatalog?.();
            this.stopWatchingCatalog = PciDataStatus.Watch(catalogFreshness, state => {
                if (!this.mounted) return;
                if (state === "expired") {
                    this.setState({
                        listCategories: [],
                        mailingLists: [],
                        isReady: false,
                        loadError: "The mailing-list catalog has reached its 15-minute limit. Refresh the page to verify current options and subscriptions."
                    });
                }
                this.setState({ catalogState: state });
            });
        } catch {
            if (!this.mounted) return;
            this.setState({
                listCategories: [],
                mailingLists: [],
                isLoading: false,
                isReady: false,
                loadError: "Mailing lists could not be loaded and your current subscriptions could not be verified. Editing is disabled."
            });
        }
    }

    private UpdateSubscribedLists(lists: Array<IMailingList>): void {
        const subscriptions = new Map(lists.map(list => [list.listName, list] as const));
        const mailingLists = this.state.mailingLists.map(list => {
            const subscription = subscriptions.get(list.listName);
            return {
                ...list,
                subscribed: subscription !== undefined,
                subscribe: subscription !== undefined,
                subscriptionDate: subscription ? new Date(subscription.subscriptionDate) : null
            };
        });

        this.setState({ mailingLists });
    }

    private GetCategoryLists(category: string, showAvailable = false) {
        return this.state.mailingLists.filter(list =>
            list.listCategory === category && (showAvailable || list.subscribed));
    }

    public SubscribeList(list: IMailingList): void {
        if (this.state.catalogState !== "fresh")
            return;

        const mailingLists = this.state.mailingLists.map(current =>
            current.listName === list.listName
                ? { ...current, subscribe: !current.subscribe }
                : current);
        this.setState({ mailingLists });
    }

    public ToggleListView(subOnly: boolean): void {
        this.setState({ subOnly });
    }

    public render() {
        const catalogStale = this.state.catalogState === "stale";

        return (
            <div id="mailing-list-subscriptions" className="mailing-list-subscriptions">
                <div className="row padding-btm-20">
                    <h3 className="col-12 blue">Mailing Lists</h3>
                </div>

                {this.state.loadError && (
                    <PciStatusAlert tone="error"
                        heading="Mailing lists are unavailable"
                        message={this.state.loadError} />
                )}

                {catalogStale && this.state.catalogFreshness && (
                    <PciStatusAlert tone="warning"
                        heading="Showing last-known mailing list options"
                        message={`The catalog was retrieved ${PciDataStatus.FormatDate(this.state.catalogFreshness.fetchedAt)}. Saving is disabled until the catalog is current. Refresh the page to check for updates.`} />
                )}

                {this.state.isReady && (
                    <>
                        <div className="row padding-btm-20">
                            <div className="tab-content-block col-12">
                                <ul className="nav nav-tabs">
                                    <li className="nav-item">
                                        <a className="nav-link active" data-bs-toggle="tab" data-bs-target="#myMailingListsTab" href="#myMailingListsTab">My Mailing Lists</a>
                                    </li>
                                    <li className="nav-item">
                                        <a className="nav-link" data-bs-toggle="tab" data-bs-target="#availableMailingListsTab" href="#availableMailingListsTab">All Available Mailing Lists</a>
                                    </li>
                                </ul>
                            </div>
                        </div>

                        <div className="tab-content">
                            <div id="myMailingListsTab" className="tab-pane fade show active in">
                                <div className="row">
                                    <div className="col-12">
                                        {this.state.listCategories.map((category, i) => (
                                            <MailingListCategory key={category.id} Category={category} Index={i}
                                                MailingLists={this.GetCategoryLists(category.name)}
                                                SubscribeList={this.SubscribeList}
                                                Disabled={catalogStale} />
                                        ))}
                                    </div>
                                </div>
                            </div>
                            <div id="availableMailingListsTab" className="tab-pane fade">
                                <div className="row">
                                    <div className="col-12">
                                        {this.state.listCategories.map((category, i) => (
                                            <MailingListCategory key={category.id} Category={category} Index={i}
                                                MailingLists={this.GetCategoryLists(category.name, true)}
                                                SubscribeList={this.SubscribeList}
                                                Disabled={catalogStale} />
                                        ))}
                                    </div>
                                </div>

                            </div>
                        </div>

                        <div className="row">
                            <div className="col-12">
                                <MailingListUpdate MailingLists={this.state.mailingLists}
                                    UpdateSubscribedLists={this.UpdateSubscribedLists}
                                    Disabled={catalogStale} />
                            </div>
                        </div>
                    </>
                )}
            </div>
        );
    }
}
